import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText,
  Paper,
  Popper,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import SignoutHeader from "../subcomponents/SignoutHeader";
import DeleteAccountModal from "../confirmationModals/deleteAccountModal";
import { PrimaryButton } from "../subcomponents/CustomButton";
import { Colors } from "../Colors";
import { FrontendUser, Organization, Agent, EHR } from "../types";
import axios from "axios";
import { useSnackbar } from "../providers/SnackbarProvider";
import UploadLogoModal from "../forms/UploadLogo";
import { QRCodeCanvas } from "qrcode.react";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { getSession, updateOrganizationAttribute } from "../slices/AuthSlice";

const Account: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { showMessage } = useSnackbar();

  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);
  const organization: Organization | null = useSelector((state: RootState) => state.auth.organization);
  const [uploadLogoModalOpen, setUploadLogoModalOpen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [selfAddUrl, setSelfAddUrl] = useState(`https://app.penciled.com/patient/self-add/${organization?.organizationId}`);
  const [loadingUploadingLogo, setLoadingUploadingLogo] = useState(false);
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [orgNameValue, setOrgNameValue] = useState(organization?.name || "");
  const [savingOrgName, setSavingOrgName] = useState(false);

  useEffect(() => {
    if (organization && organization.organizationId) {
      setSelfAddUrl(`https://app.penciled.com/patient/self-add/${organization?.organizationId}`);
      setOrgNameValue(organization.name || "");
      setEditingOrgName(false);
    }
  }, [organization?.organizationId]);

  const dispatch = useAppDispatch();

  const handleDeleteClick = () => {
    setOpenModal(true);
  };

  const handleFileChange = (file: File) => {
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      setSelectedFile(file);
      setUploadLogoModalOpen(false);
      handleUpload();
    } else {
      showMessage("Please select a JPG or PNG file", "error");
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      setLoadingUploadingLogo(true);
      const formData = new FormData();
      formData.append("logo", selectedFile);

      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/upload-logo`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${user?.token}`,
          },
        });
        showMessage("Logo uploaded successfully", "success");

        await dispatch(getSession());
      } catch (error) {
        console.error("Error uploading logo:", error);
        showMessage("Failed to upload logo", "error");
      } finally {
        setLoadingUploadingLogo(false);
      }
    } else {
      showMessage("No file selected", "error");
      setLoadingUploadingLogo(false);
    }
  };

  const handleDownloadQRCode = () => {
    if (canvasRef.current) {
      const url = canvasRef.current.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = url;
      link.download = `QRCode-${organization?.organizationId}.png`;
      link.click();
    }
  };

  const handleSaveOrgName = async () => {
    if (!user?.token || !orgNameValue.trim()) return;
    setSavingOrgName(true);
    try {
      await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "name", value: orgNameValue.trim() }));
      showMessage("Organization name updated", "success");
      setEditingOrgName(false);
    } catch {
      showMessage("Failed to update organization name", "error");
    } finally {
      setSavingOrgName(false);
    }
  };

  const handleOrgChange = async (organizationId: string) => {
    const organization = user?.organizations.find((org) => org.organizationId === organizationId);
    if (!user?.token || !organizationId || !organization) {
      return;
    }

    const superOrganizationId = organization.superOrganizationId || undefined;

    await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/api/users/update`,
      { organizationId, superOrganizationId },
      { headers: { Authorization: `Bearer ${user?.token}` } }
    );
    await dispatch(getSession());
    window.location.reload();
  };

  return (
    <Box sx={{ color: "#FFF" }}>
      <SignoutHeader />
      <Typography variant="h5" sx={{ fontSize: { xs: "1.3rem", sm: "1.8rem" }, margin: { xs: "80px 0 10px 0", lg: "20px 0" } }}>
        My Account Info
      </Typography>

      {user && user.organizations && user.organizations.length >= 1 && (
        <FormControl fullWidth sx={{ margin: "20px 0" }}>
          <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, marginBottom: "10px" }}>
            Change Organization:
          </Typography>
          <Select
            value={organization?.organizationId || ""}
            onChange={(e) => handleOrgChange(e.target.value as string)}
            inputProps={{ "aria-label": "Without label" }}
            sx={{ color: "#FFF" }}
          >
            {user.organizations.map((org) => (
              <MenuItem key={org.organizationId} value={org.organizationId}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {organization?.logoUrl && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px" }}>
          <img src={organization.logoUrl} alt="Organization Logo" style={{ maxWidth: "100px", height: "auto" }} />
        </Box>
      )}

      <UploadLogoModal open={uploadLogoModalOpen} onClose={() => setUploadLogoModalOpen(false)} onUpload={handleFileChange} />
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          Name:{" "}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          {user?.firstName} {user?.lastName}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          Email:{" "}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          {" "}
          {user?.email}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}>
          Organization:
        </Typography>
        {editingOrgName ? (
          <>
            <TextField
              value={orgNameValue}
              onChange={(e) => setOrgNameValue(e.target.value)}
              size="small"
              sx={{ input: { color: "#FFF" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.4)" } }}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveOrgName(); if (e.key === "Escape") setEditingOrgName(false); }}
              autoFocus
            />
            <Button onClick={handleSaveOrgName} disabled={savingOrgName} size="small" variant="contained" sx={{ backgroundColor: Colors.primary, "&:hover": { backgroundColor: Colors.primaryDark } }}>
              Save
            </Button>
            <Button onClick={() => { setEditingOrgName(false); setOrgNameValue(organization?.name || ""); }} size="small">
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}>
              {organization?.name || "Organization Name Not Added"}
            </Typography>
            <IconButton size="small" onClick={() => setEditingOrgName(true)} sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "#FFF" } }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          Callback Number:{" "}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          {organization?.phoneNumber || "Callback Number Not Added"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          Timezone:{" "}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, margin: { xs: "20px 0 10px 0", md: "20px 0" } }}>
          {organization?.timezone || "Timezone Not Found"}
        </Typography>
      </Box>

      {/* File Upload Section */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px", marginBottom: "30px" }}>
        {/* Button to trigger file input */}
        <Button onClick={() => setUploadLogoModalOpen(true)}>Upload Logo</Button>

        {selectedFile && <Typography variant="subtitle1">{selectedFile.name}</Typography>}
      </Box>

      {loadingUploadingLogo && <LoadingWithMessage message="Uploading logo..." size={10} />}

      {/* QR Code Section */}
      <Box sx={{ marginTop: "20px" }}>
        <Typography variant="h6" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, marginBottom: "10px" }}>
          Download QR Code for Self-Add
        </Typography>
        <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, marginBottom: "10px" }}>
          Link to self-add:{" "}
          <a href={selfAddUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#4EC5FF" }}>
            {selfAddUrl}
          </a>
        </Typography>
        {organization && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column", // Stack elements vertically
                alignItems: "left", // Center-align the elements
                marginTop: "20px",
              }}
            >
              <QRCodeCanvas
                value={selfAddUrl}
                size={128} // Adjust the size as needed
                fgColor="#000000"
                bgColor="#ffffff"
                level="L"
                includeMargin={false}
                ref={canvasRef} // Reference to the canvas element
              />
              <Button
                variant="contained"
                sx={{
                  marginTop: "10px",
                  backgroundColor: Colors.primary,
                  "&:hover": { backgroundColor: Colors.primaryDark },
                  maxWidth: "150px",
                  maxHeight: "50px",
                }}
                onClick={handleDownloadQRCode}
              >
                Download QR Code
              </Button>
            </Box>
          </>
        )}
      </Box>

      <PrimaryButton
        onClick={handleDeleteClick}
        sx={{
          padding: "16px 32px", // Adjust padding as needed
          marginTop: "20px",
          backgroundColor: Colors.error,
          "&:hover": {
            backgroundColor: Colors.errorDark,
          },
        }}
      >
        Delete Account
      </PrimaryButton>

      <DeleteAccountModal open={openModal} onClose={() => setOpenModal(false)} />
    </Box>
  );
};

export default Account;
